// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025 Collab Digital Twins

type Listener = () => void

/**
 * Ephemeral per-plugin state, keyed by plugin and then by the plugin's own key.
 *
 * A plugin's surfaces render in unrelated React subtrees — a map tool, a viewer sidebar tab,
 * a dialog and a data page have no common ancestor below the host — so they cannot share
 * state through context or props. `usePluginStore` is the persisted alternative and costs a
 * server round trip per write, which is wrong for a selection.
 *
 * Module scope rather than a provider, for the same reason: a provider would have to sit
 * above every surface, and the surfaces do not share one.
 */
class PluginStateStore {
  private values: Map<string, unknown> = new Map()
  private listeners: Map<string, Set<Listener>> = new Map()

  private static scopedKey(pluginId: string, key: string): string {
    return `${pluginId} ${key}`
  }

  get<T>(pluginId: string, key: string, initial: T): T {
    const scoped = PluginStateStore.scopedKey(pluginId, key)
    return this.values.has(scoped) ? (this.values.get(scoped) as T) : initial
  }

  set<T>(pluginId: string, key: string, value: T): void {
    const scoped = PluginStateStore.scopedKey(pluginId, key)
    if (Object.is(this.values.get(scoped), value)) return

    this.values.set(scoped, value)
    for (const listener of this.listeners.get(scoped) ?? []) listener()
  }

  subscribe(pluginId: string, key: string, listener: Listener): () => void {
    const scoped = PluginStateStore.scopedKey(pluginId, key)
    const forKey = this.listeners.get(scoped) ?? new Set<Listener>()

    forKey.add(listener)
    this.listeners.set(scoped, forKey)

    return () => {
      forKey.delete(listener)
      if (forKey.size === 0) this.listeners.delete(scoped)
    }
  }

  /**
   * Drops everything one plugin holds. Called when it is disabled or unloaded, so a plugin
   * re-enabled in the same session starts clean rather than resuming a stale selection.
   */
  clear(pluginId: string): void {
    const prefix = `${pluginId} `

    for (const scoped of [...this.values.keys()]) {
      if (!scoped.startsWith(prefix)) continue

      this.values.delete(scoped)
      for (const listener of this.listeners.get(scoped) ?? []) listener()
    }
  }
}

export const pluginStateStore = new PluginStateStore()
